import { useState } from "react";
import { isEmpty } from "lodash";
import { useFormContext, useWatch } from "react-hook-form";
import { tabs } from "../../../../constants";
import type {
  PaywallFormData,
  StripePriceAttributeProps,
  TabOption,
} from "../../../types";

interface UsePaywallFormContentCreateFlowArgs {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}
interface UsePaywallFormContentCreateFlowReturn {
  handleTabClick: (tab: string) => void;
  shouldBlockSubmitButton: boolean;
  tabOptions: TabOption[];
}

const shouldDisableTab = (
  currentTab: string,
  pastTabs: string[],
  validations: (() => boolean)[],
) =>
  pastTabs.includes(currentTab) ||
  validations.some(validation => !validation());

export const usePaywallFormContentCreateFlow = ({
  activeTab,
  setActiveTab,
}: UsePaywallFormContentCreateFlowArgs): UsePaywallFormContentCreateFlowReturn => {
  const [shouldBlockSubmitButton, setShouldBlockSubmitButton] =
    useState<boolean>(false);

  // Explicitly type the control object
  const { control, formState } = useFormContext<PaywallFormData>();

  // Type the watched values based on PaywallFormData
  const displayName = useWatch({ control, name: "display_name" });
  const checkoutPath = useWatch({ control, name: "checkout_path" });
  const webPricesAttributes = useWatch({
    control,
    name: "stripe_prices_attributes",
  }) as StripePriceAttributeProps[]; // Assert type for watched array
  const isAllSpacesAllowed = useWatch({ control, name: "all_spaces" });
  const spaceGroupIds = useWatch({ control, name: "space_group_ids" }) || [];
  const accessGroups = useWatch({ control, name: "access_groups" }) || {};
  const spaceIds = useWatch({ control, name: "space_ids" }) || [];

  const blockButtonByTab = (currentTab: string, isValid: boolean): void => {
    const hasSameValueShouldBlockSubmitButton =
      shouldBlockSubmitButton === !isValid;
    if (currentTab === activeTab && !hasSameValueShouldBlockSubmitButton) {
      setShouldBlockSubmitButton(!isValid);
    }
  };

  const isDetailsTabValid = (): boolean => {
    const isValid = !!displayName && !!checkoutPath;
    blockButtonByTab(tabs.DETAILS_TAB, isValid);
    return isValid;
  };

  const isPricingTabValid = (): boolean => {
    // Ensure webPricesAttributes is an array before calling .some()
    const isValid =
      Array.isArray(webPricesAttributes) &&
      webPricesAttributes.some(
        (priceAttribute: StripePriceAttributeProps) =>
          // Combine conditions into a single boolean expression
          (!!priceAttribute.id && !priceAttribute._destroy) ||
          !!priceAttribute.tmp_id,
      );
    blockButtonByTab(tabs.PRICING_TAB, isValid);
    return isValid;
  };

  const isAccessTabValid = (): boolean => {
    const isValid =
      !!isAllSpacesAllowed ||
      (Array.isArray(spaceGroupIds) && spaceGroupIds.length > 0) ||
      (Array.isArray(spaceIds) && spaceIds.length > 0) ||
      !isEmpty(accessGroups); // Assuming accessGroups is an object
    blockButtonByTab(tabs.ACCESS_TAB, isValid);
    return isValid;
  };

  const isPostPurchaseTabValid = (): boolean =>
    !formState?.errors["redirect_url"] &&
    !formState?.errors["post_purchase_email_meta"];

  // All tabs should be navigated through `Next` button, which enforces
  // an active tab even if it is disabled. That's why we are always disabling
  // the last step.
  const tabOptions: TabOption[] = [
    {
      label: tabs.DETAILS_TAB,
      disabled: shouldDisableTab(activeTab, [], [isDetailsTabValid]),
    },
    {
      label: tabs.PRICING_TAB,
      disabled: shouldDisableTab(
        activeTab,
        [tabs.DETAILS_TAB],
        [isDetailsTabValid, isPricingTabValid],
      ),
    },
    {
      label: tabs.ACCESS_TAB,
      disabled: shouldDisableTab(
        activeTab,
        [tabs.DETAILS_TAB, tabs.PRICING_TAB],
        [isDetailsTabValid, isPricingTabValid, isAccessTabValid],
      ),
    },
    {
      label: tabs.CHECKOUT_TAB,
      disabled: shouldDisableTab(
        activeTab,
        [tabs.DETAILS_TAB, tabs.PRICING_TAB, tabs.ACCESS_TAB],
        [isDetailsTabValid, isPricingTabValid, isAccessTabValid],
      ),
    },
    {
      label: tabs.POST_PURCHASE_TAB,
      disabled: shouldDisableTab(
        activeTab,
        [
          tabs.DETAILS_TAB,
          tabs.PRICING_TAB,
          tabs.CHECKOUT_TAB,
          tabs.ACCESS_TAB,
        ],
        [
          isDetailsTabValid,
          isPricingTabValid,
          isAccessTabValid,
          isPostPurchaseTabValid,
        ],
      ),
    },
    {
      label: tabs.TRACKING_TAB,
      disabled: true,
    },
  ];

  const handleTabClick = (tab: string): void => {
    setActiveTab(tab);
  };

  return {
    handleTabClick,
    tabOptions,
    shouldBlockSubmitButton,
  };
};
